import { useEffect } from 'react';
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@material-ui/core';
import { PHONE_REGEX } from 'config/constants';
import { generateID } from 'devextreme-react/core/helpers';
import { getDtoDateFormat, toSnakeCase } from 'utils';
import { useBookingAgenda, useBookingAgendaFunctions } from '../../contexts/BookingAgendaContext';
import { useCompany } from 'contexts/CompanyContext';
import { useFormik } from 'formik';
import { useTheme } from '@material-ui/core/styles';
import * as Yup from 'yup';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import BookingIcon from '_assets/dashboard/booking.svg';
import DefaultButton from 'components/DefaultButton';
import DefaultMenuItem from 'components/DefaultMenuItem';
import useStyles from './styles';
import { format } from 'date-fns';

const clientRequiredValidationSchema = {
  clientFirstName: Yup.string().required('Champ obligatoire'),
  clientLastName: Yup.string().required('Champ obligatoire'),
  clientEmail: Yup.string().email('Veuillez entrer un email valide').required('Champ obligatoire'),
  clientPhone: Yup.string()
    .matches(PHONE_REGEX, 'Veuillez entrer un numéro de téléphone valide')
    .required('Champ obligatoire'),
  clientGender: Yup.string()
    .oneOf(['male', 'female'], "Veuillez choisir l'une des options")
    .required('Champ obligatoire'),
};

const clientValidationSchema = {
  clientFirstName: Yup.string(),
  clientLastName: Yup.string(),
  clientEmail: Yup.string(),
  clientPhone: Yup.string(),
  clientGender: Yup.string(),
  clientBirthday: Yup.date(),
};

const paymentValidationSchema = {
  paymentMethod: Yup.string()
    .oneOf(['card', 'cash'], "Veuillez choisir l'une des options")
    .required('Champ obligatoire'),
  paymentAmount: Yup.number()
    .typeError("Veuillez utiliser le '.' pour séparer les euros des centimes.")
    .min(0, 'Le montant ne peut pas être négatif')
    .required('Champ obligatoire'),
};

export default function ExternalReservation({ client, isOpen, onClose }) {
  const classes = useStyles();
  const theme = useTheme();
  const { currentCompany } = useCompany();

  const { selectedAppointment } = useBookingAgenda();
  const { createBooking, updateBooking } = useBookingAgendaFunctions();

  const isEditing = !!client?.id;
  const validationSchema = Yup.object({
    ...paymentValidationSchema,
    ...(isEditing ? clientValidationSchema : clientRequiredValidationSchema),
  }).defined();

  clientRequiredValidationSchema.clientBirthday = Yup.date()
    .required('Champ obligatoire')
    .test('has-good-age', "Le client doit être dans la tranche d'age de l'activité pour réserver.", (value) => {
      const age = new Date(new Date() - value).getFullYear() - new Date(0).getFullYear();
      return age >= selectedAppointment?.service.minAge && age <= selectedAppointment?.service.maxAge;
    });

  const { errors, handleChange, handleSubmit, isSubmitting, resetForm, touched, values } = useFormik({
    validationSchema,
    initialValues: {
      clientEmail: client?.email || '',
      clientFirstName: client?.firstName || '',
      clientGender: client?.gender || '',
      clientLastName: client?.lastName || '',
      clientPhone: client?.phone || '',
      paymentAmount: client?.price || selectedAppointment?.service?.currentPrice || '',
      paymentMethod: client?.paymentMethod || '',
      clientBirthday: client?.birthday || format(new Date(), 'yyyy-MM-dd'),
    },
    onSubmit: async (values) => {
      let result = false;
      try {
        if (isEditing) {
          result = await updateBooking(client.bookingId, values);
        } else {
          const data = {
            amount_before_tax: values.paymentAmount / 1.2,
            balance: 0,
            order_number: generateID(),
            service_id: selectedAppointment?.service.id,
            source: 'Maïa Partners',
            state: 'paid',
            total_amount: values.paymentAmount,
            VAT: 20,
            company_id: currentCompany.id,
          };

          const formatStartDate = getDtoDateFormat(selectedAppointment.startDate);
          const formatEndDate = getDtoDateFormat(selectedAppointment.endDate);
          values.clientBirthday = getDtoDateFormat(values.clientBirthday);

          const body = {
            ...toSnakeCase(values),
            service_id: selectedAppointment?.service.id,
            start_date: formatStartDate,
            end_date: formatEndDate,
            bill_data: data,
          };

          const { success } = await createBooking(body);

          result = success;
        }

        if (!result) return;

        resetForm();
        onClose?.();
      } catch (_) {}
    },
  });

  useEffect(() => {
    resetForm({
      values: {
        clientEmail: client?.email || '',
        clientFirstName: client?.firstName || '',
        clientGender: client?.gender || '',
        clientLastName: client?.lastName || '',
        clientPhone: client?.phone || '',
        clientBirthday: client?.birthday || format(new Date(), 'yyyy-MM-dd'),
        paymentAmount: client?.price || selectedAppointment?.service?.currentPrice || '',
        paymentMethod: client?.paymentMethod || '',
      },
    });
  }, [client, resetForm, selectedAppointment?.service?.currentPrice]);

  function handleClose() {
    if (isSubmitting) return;
    resetForm();
    onClose?.();
  }

  return (
    <Dialog className={classes.root} open={isOpen} onClose={handleClose} aria-labelledby="external-reservation">
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Grid className={classes.contentRoot} container justifyContent="center">
            <Grid className={classes.header} container item xs={12}>
              <Grid className={classes.iconContainer} container item xs={2} alignItems="center">
                <IconButton onClick={handleClose}>
                  <ArrowBackIcon style={{ color: theme.palette.text.title }} />
                </IconButton>
              </Grid>

              <Grid container item xs={12} alignItems="center" justifyContent="center">
                <img src={BookingIcon} alt="réservation" />
                <Typography className={classes.title} variant="h4">
                  Faire une réservation externe
                </Typography>
              </Grid>
            </Grid>

            <Grid className={classes.formContainer} container item xs={12} justifyContent="center" spacing={2}>
              <Grid container item xs={12} spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    disabled={isEditing}
                    error={touched.clientFirstName && Boolean(errors.clientFirstName)}
                    helperText={touched.clientFirstName && errors.clientFirstName}
                    fullWidth
                    label="Prénom"
                    name="clientFirstName"
                    variant="outlined"
                    onChange={handleChange}
                    value={values.clientFirstName}
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    disabled={isEditing}
                    error={touched.clientLastName && Boolean(errors.clientLastName)}
                    helperText={touched.clientLastName && errors.clientLastName}
                    fullWidth
                    label="Nom"
                    name="clientLastName"
                    variant="outlined"
                    onChange={handleChange}
                    value={values.clientLastName}
                  />
                </Grid>
              </Grid>

              <Grid container item xs={12} spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    disabled={isEditing}
                    error={touched.clientEmail && Boolean(errors.clientEmail)}
                    helperText={touched.clientEmail && errors.clientEmail}
                    fullWidth
                    label="Email"
                    name="clientEmail"
                    variant="outlined"
                    onChange={handleChange}
                    value={values.clientEmail}
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    disabled={isEditing}
                    error={touched.clientPhone && Boolean(errors.clientPhone)}
                    helperText={touched.clientPhone && errors.clientPhone}
                    fullWidth
                    label="Téléphone"
                    name="clientPhone"
                    variant="outlined"
                    onChange={handleChange}
                    value={values.clientPhone}
                  />
                </Grid>
              </Grid>

              <Grid container item xs={12} spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    error={touched.paymentMethod && Boolean(errors.paymentMethod)}
                    helperText={touched.paymentMethod && errors.paymentMethod}
                    fullWidth
                    label="Type de paiement"
                    name="paymentMethod"
                    select
                    variant="outlined"
                    value={values.paymentMethod}
                    onChange={handleChange}
                  >
                    <DefaultMenuItem value="card">Carte</DefaultMenuItem>
                    <DefaultMenuItem value="cash">Espèces</DefaultMenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    error={touched.paymentAmount && Boolean(errors.paymentAmount)}
                    helperText={touched.paymentAmount && errors.paymentAmount}
                    InputProps={{ inputProps: { min: 0 } }}
                    fullWidth
                    label="Total TTC à payer"
                    name="paymentAmount"
                    type="number"
                    variant="outlined"
                    onChange={handleChange}
                    value={values.paymentAmount}
                  />
                </Grid>
              </Grid>

              <Grid container item xs={12} spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    disabled={isEditing}
                    error={touched.clientGender && Boolean(errors.clientGender)}
                    helperText={touched.clientGender && errors.clientGender}
                    fullWidth
                    label="Genre"
                    name="clientGender"
                    select
                    variant="outlined"
                    value={values.clientGender}
                    onChange={handleChange}
                  >
                    <DefaultMenuItem value="male">Homme</DefaultMenuItem>
                    <DefaultMenuItem value="female">Femme</DefaultMenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    disabled={isEditing}
                    error={touched.clientBirthday && Boolean(errors.clientBirthday)}
                    helperText={touched.clientBirthday && errors.clientBirthday}
                    fullWidth
                    label="Date de naissance"
                    name="clientBirthday"
                    type={'date'}
                    variant="outlined"
                    value={values.clientBirthday}
                    onChange={handleChange}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions classes={{ root: classes.dialogActions }}>
          <DefaultButton
            color="secondary"
            endIcon={<ArrowForwardIcon />}
            type="submit"
            style={{ fontSize: 16 }}
            variant="outlined"
          >
            {isSubmitting ? <CircularProgress color="secondary" size={20} /> : 'Réserver'}
          </DefaultButton>
        </DialogActions>
      </form>
    </Dialog>
  );
}
